/* Product page behaviour.
   <variant-picker>  resolves the variant from the option radios, updates price,
                     availability text, add-to-cart state, URL, gallery group,
                     lining card prices, sticky bar.
   <product-gallery> desktop: click thumb → active; toggles variant groups.
   <sticky-atc>      IntersectionObserver on the main add-to-cart button. */

function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const [int, dec] = value.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const f = format || '{{amount}} CHF';
  if (f.includes('amount_no_decimals')) return f.replace(/\{\{\s*amount_no_decimals\s*\}\}/, grouped).replace(/<[^>]+>/g, '');
  return f.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, `${grouped}.${dec}`).replace(/<[^>]+>/g, '');
}

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
    this.page = this.closest('product-page');
    this.colourIndex = Number(this.dataset.colourIndex) - 1; // -1 if none
    this.addEventListener('change', (e) => { if (e.target.matches('.variants__input')) this.update(); });
    this.markAvailability();
    this.updateCardPrices();
  }

  selectedOptions() {
    return Array.from(this.querySelectorAll('.variants__group')).map((g) => g.querySelector('.variants__input:checked')?.value ?? null);
  }

  find(options) { return this.variants.find((v) => v.options.every((o, i) => o === options[i])); }

  update() {
    const options = this.selectedOptions();
    const variant = this.find(options);
    this.querySelectorAll('.variants__group').forEach((g, i) => { const v = g.querySelector('[data-selected-value]'); if (v) v.textContent = options[i] || ''; });
    /* Sentence for the selected leather colour */
    const descEl = this.querySelector('[data-colour-desc]'), descJson = this.querySelector('[data-colour-descs]');
    if (descEl && descJson && this.colourIndex >= 0) {
      const map = JSON.parse(descJson.textContent); const text = map[options[this.colourIndex]] || '';
      descEl.textContent = text; descEl.hidden = !text;
    }
    this.markAvailability();
    this.updateCardPrices();
    this.render(variant);
  }

  /* Hide option values that lead to no available variant given the other
     selections (merchant rule: unavailable = not shown). If the checked value
     disappears, the first visible value in that group is selected. */
  markAvailability() {
    const options = this.selectedOptions();
    let reselected = false;
    this.querySelectorAll('.variants__group').forEach((group, gi) => {
      let needsReselect = false;
      group.querySelectorAll('.variants__input').forEach((input) => {
        const probe = options.slice(); probe[gi] = input.value;
        const v = this.find(probe);
        const hide = !v || !v.available;
        input.hidden = hide;
        if (input.nextElementSibling) input.nextElementSibling.hidden = hide;
        if (hide && input.checked) { input.checked = false; needsReselect = true; }
      });
      if (needsReselect) {
        const first = group.querySelector('.variants__input:not([hidden])');
        if (first) { first.checked = true; reselected = true; }
      }
    });
    if (reselected) this.markAvailability();
  }

  /* Absolute price per lining card for the currently selected colour */
  updateCardPrices() {
    const options = this.selectedOptions();
    this.querySelectorAll('.variants__group--card').forEach((group) => {
      const gi = Number(group.dataset.optionIndex) - 1;
      group.querySelectorAll('.variants__input').forEach((input) => {
        const probe = options.slice(); probe[gi] = input.value;
        const v = this.find(probe);
        const el = input.nextElementSibling?.querySelector('[data-option-price]');
        if (el && v) el.textContent = formatMoney(v.price, this.strings.money_format);
      });
    });
  }

  render(variant) {
    const page = this.page;
    const idInput = page.querySelector('[data-variant-id]');
    const addBtns = page.querySelectorAll('[data-add-button], [data-sticky-button]');
    const labels = page.querySelectorAll('[data-add-label]');
    const availability = page.querySelector('[data-availability]');
    const priceWrap = page.querySelector('.product__price');
    const stickyPrice = page.querySelector('[data-sticky-price]');
    const stickyImg = page.querySelector('[data-sticky-image]');

    if (!variant) {
      addBtns.forEach((b) => (b.disabled = true));
      labels.forEach((l) => (l.textContent = this.strings.unavailable));
      if (availability) { availability.textContent = this.strings.unavailable; availability.classList.add('buy__availability--out'); }
      return;
    }
    if (idInput) idInput.value = variant.id;
    const preorder = variant.available && variant.managed && variant.qty <= 0;
    addBtns.forEach((b) => (b.disabled = !variant.available));
    labels.forEach((l) => (l.textContent = !variant.available ? this.strings.sold_out : preorder ? this.strings.preorder_button : this.strings.add));
    if (availability) {
      availability.textContent = !variant.available ? this.strings.sold_out : preorder ? this.strings.preorder : this.strings.in_stock;
      availability.classList.toggle('buy__availability--out', !variant.available);
    }
    if (priceWrap) {
      const onSale = variant.compare > variant.price;
      priceWrap.innerHTML = `<div class="price product__price-inner${onSale ? ' price--sale' : ''}"><span class="price__current">${formatMoney(variant.price, this.strings.money_format)}</span>${onSale ? `<s class="price__compare">${formatMoney(variant.compare, this.strings.money_format)}</s>` : ''}</div>`;
    }
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, this.strings.money_format);

    const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);

    page.querySelector('product-gallery')?.showGroup(variant.media);

    /* Sticky thumbnail follows the selected colour. Not every variant carries
       its own image (e.g. a lining option), so fall back to another variant of
       the same colour rather than leaving the previous colour's photo. */
    if (stickyImg) {
      let src = variant.image;
      if (!src && this.colourIndex >= 0) {
        const colour = variant.options[this.colourIndex];
        src = this.variants.find((v) => v.image && v.options[this.colourIndex] === colour)?.image || null;
      }
      if (src && src !== stickyImg.getAttribute('src')) stickyImg.src = src;
    }
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: variant }));
  }
}

/* Leather-care add-on: picks the care product by the selected leather colour */
class CareUpsell extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-care]').textContent);
    this.check = this.querySelector('[data-addon-id]');
    this.closest('product-page')?.addEventListener('variant:change', (e) => this.update(e.detail));
    const btn = this.querySelector('[data-care-info-toggle]'), info = this.querySelector('[data-care-info]');
    btn?.addEventListener('click', () => { info.hidden = !info.hidden; btn.setAttribute('aria-expanded', String(!info.hidden)); });
  }
  update(variant) {
    const ci = Number(this.dataset.colourIndex) - 1;
    const colour = (variant.options[ci] || '').toLowerCase().trim();
    const norm = (l) => (l || []).map((c) => c.trim());
    const type = norm(this.data.anilin_colours).includes(colour) ? 'anilin' : norm(this.data.nubuk_colours).includes(colour) ? 'nubuk' : null;
    const care = type ? this.data[type] : null;
    if (!care || !care.available) { this.hidden = true; this.check.checked = false; return; }
    this.hidden = false;
    this.check.dataset.addonId = care.id;
    this.querySelector('[data-care-title]').textContent = care.title;
    this.querySelector('[data-care-price]').textContent = '+ ' + care.price;
    const img = this.querySelector('[data-care-image]');
    if (img && img.tagName === 'IMG' && care.image) { img.src = care.image; img.srcset = ''; }
    const info = this.querySelector('[data-care-info]');
    if (info) info.textContent = care.info || '';
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('.gallery__group'));
    this.groups.forEach((g) => this.activate(g, g.querySelector('.gallery__slide')));
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-gallery-prev]')) return this.step(-1);
      if (e.target.closest('[data-gallery-next]')) return this.step(1);
      const slide = e.target.closest('.gallery__slide');
      if (slide && !slide.classList.contains('is-active')) this.activate(slide.closest('.gallery__group'), slide);
    });
    /* Keyboard: ← → when the gallery has focus */
    this.tabIndex = -1;
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
    });
    /* Mobile: keep the nav in sync with the scroll-snap position */
    this.querySelectorAll('.gallery__track').forEach((track) => {
      track.addEventListener('scroll', () => this.syncFromScroll(track), { passive: true });
    });
    this.positionNav();
    window.addEventListener('resize', () => this.positionNav(), { passive: true });
  }
  get current() { return this.groups.find((g) => !g.hidden); }
  slides(group) { return Array.from(group.querySelectorAll('.gallery__slide')); }
  isDesktop() { return window.matchMedia('(min-width: 1024px)').matches; }
  activate(group, slide) {
    if (!group || !slide) return;
    this.slides(group).forEach((s) => s.classList.toggle('is-active', s === slide));
    this.updateNav(group);
  }
  step(dir) {
    const group = this.current; if (!group) return;
    const slides = this.slides(group);
    const i = slides.findIndex((s) => s.classList.contains('is-active'));
    const next = Math.min(slides.length - 1, Math.max(0, i + dir));
    if (next === i) return;
    if (this.isDesktop()) {
      this.activate(group, slides[next]);
      this.positionNav();
    } else {
      const track = group.querySelector('.gallery__track');
      const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      track.scrollTo({ left: slides[next].offsetLeft - pad });  /* smoothness via CSS scroll-behavior */
      this.activate(group, slides[next]);
    }
  }
  syncFromScroll(track) {
    if (this.isDesktop()) return;
    const slides = Array.from(track.querySelectorAll('.gallery__slide'));
    const x = track.scrollLeft + track.clientWidth * 0.1;
    const cur = slides.reduce((acc, s) => (s.offsetLeft <= x ? s : acc), slides[0]);
    if (cur) { slides.forEach((s) => s.classList.toggle('is-active', s === cur)); this.updateNav(track.closest('.gallery__group')); }
  }
  updateNav(group) {
    const slides = this.slides(group);
    const i = slides.findIndex((s) => s.classList.contains('is-active'));
    const prev = this.querySelector('[data-gallery-prev]'), next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= slides.length - 1;
    if (prev) prev.hidden = next.hidden = slides.length < 2;
  }
  /* Centre the arrows vertically on the main image */
  positionNav() {
    const active = this.current?.querySelector('.gallery__slide.is-active');
    if (!active) return;
    const top = active.offsetTop + active.offsetHeight / 2;
    this.querySelectorAll('.gallery__nav').forEach((b) => (b.style.top = `${top}px`));
  }
  /* Show the group whose start position is the largest one <= media position */
  showGroup(mediaPosition) {
    if (!this.groups.length) return;
    const starts = this.groups.map((g) => Number(g.dataset.group));
    let target = starts[0];
    starts.forEach((s) => { if (s <= mediaPosition && s > target) target = s; });
    if (!mediaPosition) target = starts[0];
    this.groups.forEach((g) => { g.hidden = Number(g.dataset.group) !== target; });
    const shown = this.groups.find((g) => !g.hidden);
    /* First image of the newly shown group: load now, not lazily */
    const firstImg = shown?.querySelector('.gallery__slide img');
    if (firstImg) { firstImg.loading = 'eager'; firstImg.fetchPriority = 'high'; }
    if (shown) { this.activate(shown, shown.querySelector('.gallery__slide')); shown.querySelector('.gallery__track')?.scrollTo({ left: 0 }); requestAnimationFrame(() => this.positionNav()); }
  }
}

class StickyAtc extends HTMLElement {
  connectedCallback() {
    /* Observe the buy block (not the button: third-party scripts may hide it). */
    const target = document.querySelector(this.dataset.target) || document.querySelector('product-form');
    if (!target || !('IntersectionObserver' in window)) return;
    this.hidden = false;  // becomes visible only via .is-visible transform
    const io = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      this.classList.toggle('is-visible', show);
      document.body.classList.toggle('has-sticky-atc', show);
    }, { threshold: 0 });
    io.observe(target);
  }
}

/* Quantity stepper on the product form (cart.js handles cart lines) */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('product-form [data-step]');
  if (!btn) return;
  const input = btn.parentElement.querySelector('input');
  input.value = Math.max(Number(input.min || 1), Number(input.value) + Number(btn.dataset.step));
});

if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
if (!customElements.get('care-upsell')) customElements.define('care-upsell', CareUpsell);
